import chunk from "lodash/chunk";
import { Trello } from "./trello";
import { IOtherBoards } from "./types";
import useRestApiClient, {
  CustomAPICallError,
  TTrelloClient,
} from "./_use-rest-api/use-rest-api-client";

const cardsFields =
  `badges,closed,dateLastActivity,desc,descData,due,email,` +
  `idBoard,idChecklists,idLabels,idList,idMembers,` +
  `idShort,idAttachmentCover,manualCoverAttachment,labels,name,pos,shortUrl,url,` +
  `dueReminder,idMembersVoted,shortLink,isTemplate,dueComplete,limits,cover`;

export type TCardsFetchedInBatch = {
  valid: Trello.Card[];
  idsRemoved: string[];
  idsUnauthorized: string[];
  idsWithError: string[];
};

const useRestApi = () => {
  const _client = useRestApiClient();

  const client = {
    trelloRestAPI: async (
      ...args: Parameters<TTrelloClient["trelloRestAPI"]>
    ) => {
      return (await _client).trelloRestAPI(...args);
    },
  };

  const fetchBoard = async (
    idBoard: string,
    lists: "all" | "closed" | "none" | "open" = "none"
  ): Promise<Trello.Board> => {
    return client.trelloRestAPI("get", `/boards/${idBoard}`, { lists });
  };

  const fetchCard = async (
    cardId: string,
    withBoard = false,
    withList = false,
    quietErrors?: (new (...args: any[]) => CustomAPICallError)[]
  ): Promise<Trello.Card> => {
    const card = await client.trelloRestAPI(
      "get",
      `/cards/${cardId}`,
      {
        fields: cardsFields,
        members: true,
        attachments: true,
        checklists: "all",
        stickers: true,
        board: withBoard,
        list: withList,
      },
      quietErrors
    );
    return card;
  };

  const fetchCardsInBatches = async (
    idsCards: string[],
    withBoard = false,
    withList = false,
    quietErrors?: (new (...args: any[]) => CustomAPICallError)[]
  ): Promise<TCardsFetchedInBatch> => {
    const queryParams = {
      fields: cardsFields,
      members: true,
      attachments: true,
      checklists: "all",
      stickers: true,
      board: withBoard,
      list: withList,
    };

    const queryString = Object.keys(queryParams)
      .map((key) => {
        return (
          encodeURIComponent(key) + "=" + encodeURIComponent(queryParams[key])
        );
      })
      .join("&");

    const batchesOf10 = chunk(
      idsCards.map((id) => ({
        url: `/cards/${id}?${queryString}`,
        idCard: id,
      })),
      10
    );

    const results = await Promise.all(
      batchesOf10.map(async (batch) => {
        const result = await client.trelloRestAPI(
          "get",
          `/batch/`,
          { urls: batch.map((x) => x.url) },
          quietErrors
        );
        return mapBatchCardsFetchResults(result, batch);
      })
    );

    const output = results.reduce(
      (acc, curr) => ({
        valid: acc.valid.concat(curr.valid),
        idsRemoved: acc.idsRemoved.concat(curr.idsRemoved),
        idsUnauthorized: acc.idsUnauthorized.concat(curr.idsUnauthorized),
        idsWithError: acc.idsWithError.concat(curr.idsWithError),
      }),
      { valid: [], idsRemoved: [], idsUnauthorized: [], idsWithError: [] }
    );

    return output;
  };

  const fetchAllBoardsForMember = async (
    idMember: string
  ): Promise<IOtherBoards[]> => {
    const result = await client.trelloRestAPI(
      "get",
      `/members/${idMember}/boards`,
      { filter: "open", fileds: "all", organization: true, lists: "open" }
    );
    return mapToBoardsWithOrganizations(result);
  };

  const fetchAttachmentsForCard = async (
    cardId: string
  ): Promise<Trello.Attachment[]> => {
    const attachments: Trello.Attachment[] = await client.trelloRestAPI(
      "get",
      `/cards/${cardId}/attachments`,
      { fields: "all" }
    );
    return attachments;
  };

  const addAttachmentsToCard = async (
    cardId: string,
    url: string
  ): Promise<Trello.Attachment> => {
    const attachment: Trello.Attachment = await client.trelloRestAPI(
      "post",
      `/cards/${cardId}/attachments`,
      { url }
    );
    return attachment;
  };

  const removeAttachmentsFromCard = async (
    cardId: string,
    idAttachment: string
  ): Promise<any> => {
    return client.trelloRestAPI(
      "delete",
      `/cards/${cardId}/attachments/${idAttachment}`
    );
  };

  return {
    fetchBoard,
    fetchCard,
    fetchCardsInBatches,
    fetchAllBoardsForMember,
    removeAttachmentsFromCard,
    addAttachmentsToCard,
    fetchAttachmentsForCard,
  };
};

export default useRestApi;

function mapBatchCardsFetchResults(
  result: any[],
  batch: { url: string; idCard: string }[]
): TCardsFetchedInBatch {
  return {
    valid: result.map((x) => x[200]).filter((x) => !!x),
    idsRemoved: result.reduce((acc, curr, idx) => {
      curr.statusCode === 404 && acc.push(batch[idx].idCard);
      return acc;
    }, []),
    idsUnauthorized: result.reduce((acc, curr, idx) => {
      curr.statusCode === 401 && acc.push(batch[idx].idCard);
      return acc;
    }, []),
    idsWithError: result.reduce((acc, curr, idx) => {
      !curr[200] &&
        curr.statusCode !== 401 &&
        curr.statusCode !== 404 &&
        acc.push(batch[idx].idCard);
      return acc;
    }, []),
  };
}

function mapToBoardsWithOrganizations(rawBoards): IOtherBoards[] {
  let output = [];
  output = rawBoards.map((b) => {
    return {
      id: b.id,
      name: b.name,
      isPersonal: !b.idOrganization && true,
      isGuest: b.idOrganization ? !b.organization : false,
      organization: b.idOrganization && {
        id: b.idOrganization,
        name: b.organization ? b.organization.displayName : undefined,
      },
      lists: b.lists,
    };
  });
  return output;
}
